/**
 * Maps flat address fields into intake form responses keyed by question ID.
 *
 * Shared by:
 * - saveOCRresponsesToIntakeAddress (OCR prefill)
 * - GO_TO_UPDATE parent handler (returning patient review)
 *
 * Superscript IDs: 6=street, 15=city, 16=state, 17=zip, 27=line2
 */

interface IntakeQuestion {
  id: number;
  superscriptIntakeQuestion?: { id: number };
}

interface IntakeFormSection {
  sectionName: string;
  questions: Array<{ intakeQuestion: IntakeQuestion }>;
}

interface AddressData {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  line2?: string | null;
}

const SUPERSCRIPT_IDS = {
  street: 6,
  city: 15,
  state: 16,
  zip: 17,
  line2: 27,
} as const;

/**
 * Find the intake question ID for a given superscript ID within Address sections.
 */
function findQuestionId(
  addressQuestions: IntakeQuestion[],
  superscriptId: number,
): string | undefined {
  return addressQuestions
    .find((q) => q.superscriptIntakeQuestion?.id === superscriptId)
    ?.id?.toString();
}

/**
 * Merge address data into an existing responses object, keyed by intake question ID.
 * Returns a new responses object (does not mutate the input).
 */
export function mapAddressToResponses(
  intakeFormSections: IntakeFormSection[] | undefined,
  existingResponses: Record<string, unknown>,
  address: AddressData,
): Record<string, unknown> {
  if (!intakeFormSections) return existingResponses;

  const addressQuestions = intakeFormSections
    .filter((s) => s.sectionName === "Address")
    .flatMap((s) => s.questions.map((q) => q.intakeQuestion));

  if (!addressQuestions.length) return existingResponses;

  const merged = { ...existingResponses };

  const mapping: Array<
    [keyof typeof SUPERSCRIPT_IDS, string | null | undefined]
  > = [
    ["street", address.street],
    ["city", address.city],
    ["state", address.state],
    ["zip", address.zip],
    ["line2", address.line2],
  ];

  for (const [key, value] of mapping) {
    if (!value) continue;
    const qId = findQuestionId(addressQuestions, SUPERSCRIPT_IDS[key]);
    if (qId) merged[qId] = value;
  }

  return merged;
}

/**
 * Extract address fields from a responses object by reading the intake
 * questions in any Address section. Inverse of `mapAddressToResponses` —
 * used on the edit return path to sync freshly-edited address values
 * back into parent-context `_consumer*` display fields.
 *
 * Returns only the keys that had non-empty string values, so callers
 * can spread safely without clobbering existing values.
 */
export function extractAddressFromResponses(
  intakeFormSections: IntakeFormSection[] | undefined,
  responses: Record<string, unknown> | undefined,
): {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  line2?: string;
} {
  if (!intakeFormSections || !responses) return {};

  const addressQuestions = intakeFormSections
    .filter((s) => s.sectionName === "Address")
    .flatMap((s) => s.questions.map((q) => q.intakeQuestion));

  if (!addressQuestions.length) return {};

  const extracted: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    line2?: string;
  } = {};

  for (const key of Object.keys(SUPERSCRIPT_IDS) as Array<
    keyof typeof SUPERSCRIPT_IDS
  >) {
    const qId = findQuestionId(addressQuestions, SUPERSCRIPT_IDS[key]);
    if (!qId) continue;
    const value = responses[qId];
    if (typeof value === "string" && value.length > 0) {
      extracted[key] = value;
    }
  }

  return extracted;
}

/**
 * Remove all address-related responses keyed by superscript IDs.
 * Used when clearing OCR-derived data after skipping photo capture.
 */
export function clearAddressResponses(
  intakeFormSections: IntakeFormSection[] | undefined,
  existingResponses: Record<string, unknown>,
): Record<string, unknown> {
  if (!intakeFormSections) return existingResponses ?? {};

  const addressQuestions = intakeFormSections
    .filter((s) => s.sectionName === "Address")
    .flatMap((s) => s.questions.map((q) => q.intakeQuestion));

  if (!addressQuestions.length) return existingResponses;

  const cleared = { ...existingResponses };

  for (const superscriptId of Object.values(SUPERSCRIPT_IDS)) {
    const qId = findQuestionId(addressQuestions, superscriptId);
    if (qId) delete cleared[qId];
  }

  return cleared;
}
